import type React from 'react';
import { useMemo } from 'react';

import type { ColumnDef } from '@tanstack/react-table';
import type {
  DepartmentId, HandlerId, HandlerRecord,
} from '@vlabs/api/artemy/model';
import { TableView } from '@vlabs/monokit/shared/ui/table-view';
import { useHasPermission } from '@vlabs/permissions';

import { DeleteHandlerIconButton } from '../delete-handler';
import { GoToHandlerIconButton } from '../go-to-handler';
import { useHandlerListTranslation } from '../handlerListI18n';
import {
  actionCell,
  descriptionCell,
  dividerCell,
  handlerIdCell,
  ownerDepartmentCell,
  renderCell,
  renderHeader,
} from './cells';
import { OwnerDepartmentCell } from './owner-department-cell/OwnerDepartmentCell';

interface HandlerListTableViewProps {
  data: HandlerRecord[];
}

function HandlerListTableView({
  data,
}: HandlerListTableViewProps): React.JSX.Element {
  const { t } = useHandlerListTranslation();
  const canDelete = useHasPermission('handler', 'delete');
  const canUpdate = useHasPermission('handler', 'update');

  const columns = useMemo<ColumnDef<HandlerRecord>[]>(() => ([
    {
      accessorKey: 'description',
      header: renderHeader(descriptionCell, t('columns.description')),
      cell: renderCell(descriptionCell),
    },
    {
      accessorKey: 'handler_id',
      header: renderHeader(handlerIdCell, t('columns.handler_id')),
      cell: renderCell(handlerIdCell),
    },
    {
      accessorKey: 'owner_department_id',
      header: renderHeader(
        ownerDepartmentCell,
        t('columns.owner_department'),
      ),
      cell: renderCell<DepartmentId>(ownerDepartmentCell, (context) => (
        <OwnerDepartmentCell ownerDepartmentId={context.getValue()} />
      )),
    },
    {
      accessorKey: 'handler_id' as const,
      id: 'editHandler',
      header: renderHeader(actionCell),
      cell: renderCell<HandlerId>(actionCell, (context) => (
        <GoToHandlerIconButton
          data-testid={context.row.original.handler_id}
          handlerId={context.getValue()}
        />
      )),
    },
    {
      accessorKey: 'handler_id' as const,
      id: 'deleteHandler',
      header: renderHeader(actionCell),
      cell: renderCell<HandlerId>(actionCell, (context) => (
        <DeleteHandlerIconButton
          data-testid={context.row.original.handler_id}
          handlerId={context.getValue()}
        />
      )),
    },
    { cell: () => dividerCell(undefined), id: 'divider' },
  ]), [t]);

  return (
    <TableView<HandlerRecord>
      columns={columns}
      data={data}
      getRowId={(handler) => handler.handler_id}
      state={{
        columnVisibility: {
          editHandler: canUpdate,
          deleteHandler: canDelete,
        },
      }}
    />
  );
}

export { HandlerListTableView };
